import { useAuth } from '@/core/auth';
import { Button } from '@/design-system';
import { cn } from '@/design-system/utils/cn';
import { Bell, LogOut, Menu, ShieldCheck, User } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
  title: string;
  onMenuToggle?: () => void;
  isSidebarCollapsed?: boolean;
}

export function Header({ title, onMenuToggle, isSidebarCollapsed = false }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setShowUserMenu(false);
  };

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header
      className={cn(
        'fixed top-0 z-30 flex w-full flex-col border-b border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60',
        'lg:transition-all lg:duration-300',
        isSidebarCollapsed ? 'lg:left-20 lg:w-[calc(100%-5rem)]' : 'lg:left-64 lg:w-[calc(100%-16rem)]'
      )}
    >
      <div className="flex h-16 items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-4">
          {onMenuToggle && (
            <Button variant="ghost" size="sm"
              onClick={onMenuToggle}
              className="lg:hidden p-2 text-gray-600"
              aria-label="Abrir menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">{title}</h1>
        </div>

        <div className="flex items-center gap-2 md:gap-4">
          <Button variant="ghost" size="sm"
            className="relative p-2 text-gray-600 transition-colors"
            aria-label="Notificações"
          >
            <Bell size={20} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
          </Button>

          <div className="h-8 w-px bg-gray-200 mx-1" />

          <div className="relative" ref={menuRef}>
            <Button variant="ghost" size="sm"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-expanded={showUserMenu}
              aria-haspopup="menu"
              className="h-auto gap-2 pl-2 pr-3 py-2 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <div className="w-9 h-9 rounded-xl bg-[#0084FF] flex items-center justify-center shrink-0">
                <User size={18} className="text-white" />
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-gray-900 leading-tight">{user?.name || 'Admin'}</p>
                <p className="text-xs text-gray-500 leading-tight">{user?.role}</p>
              </div>
            </Button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-1.5 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{user?.name || 'Admin'}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email || 'admin@orya.com'}</p>
                  <span className="inline-block mt-1.5 px-2 py-0.5 text-xs font-medium text-[#0084FF] bg-[#0084FF]/10 rounded-md">
                    {user?.role}
                  </span>
                </div>
                <Button variant="ghost" size="sm"
                  onClick={() => {
                    navigate('/security');
                    setShowUserMenu(false);
                  }}
                  className="w-full justify-start px-4 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <ShieldCheck size={16} className="text-gray-400" />
                  Segurança (2FA)
                </Button>
                <Button variant="ghost" size="sm"
                  onClick={handleLogout}
                  className="w-full justify-start px-4 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <LogOut size={16} className="text-gray-400" />
                  Sair
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
